import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import Logo from '../assest/sgu_logo.png';
import displayINRCurrency from '../helpers/displayCurrency';

const StudentInvoice = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const student = location.state?.student;
  const payment = location.state?.payment;
  const isHistory = location.state?.isHistory || false;
  const [copyType, setCopyType] = React.useState('Admin Copy');

  if (!student) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-500 font-semibold">No student data found.</p>
        <button
          onClick={() => navigate('/')}
          className="mt-4 text-blue-600 underline"
        >
          Go back
        </button>
      </div>
    );
  }

  // Use payment data if available, otherwise use student data
  const displayData = payment || student;

  const handleDownloadPDF = () => {
    const element = document.getElementById('invoice-content');
    const options = {
      margin: 0.5,
      filename: `Invoice_${student.full_name.replace(/\s/g, '_')}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
    };
    html2pdf().from(element).set(options).save();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handlePrint = () => {
    const printStyle = `
      @media print {
        body * {
          visibility: hidden;
        }
        #invoice-content, #invoice-content * {
          visibility: visible;
        }
        #invoice-content {
          margin-top: -50px
        }
      }
    `;
    
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = printStyle;
    document.head.appendChild(styleSheet);
    
    window.print();
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      {/* Buttons */}
      <div className="print:hidden flex justify-center gap-4 mb-6 flex-wrap">
        <button
          onClick={handlePrint}
          className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
        >
          Print
        </button>
        <button
          onClick={handleDownloadPDF}
          className="bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700"
        >
          Download PDF
        </button>
        <button
          onClick={() => navigate(-1)}
          className="bg-gray-600 text-white py-2 px-4 rounded hover:bg-gray-700"
        >
          Back
        </button>
      </div>

      <div className="print:hidden mb-4 text-center">
        <div className="flex justify-center gap-6 flex-wrap">
          {['Admin Copy', 'Accountant Copy', 'Student Copy'].map((type) => (
            <label key={type} className="flex items-center gap-2">
              <input
                type="radio"
                name="copyType"
                value={type}
                checked={copyType === type}
                onChange={(e) => setCopyType(e.target.value)}
                className="accent-blue-600"
              />
              {type}
            </label>
          ))}
        </div>
        {isHistory && (
          <p className="text-sm text-blue-600 mt-2 font-semibold">
            * This invoice is from payment history
          </p>
        )}
      </div>

      {/* Invoice Content */}
      <div
        id="invoice-content"
        className="max-w-3xl mx-auto bg-white border border-gray-300 rounded shadow p-8"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <img src={Logo} alt="Logo" className="mx-auto h-28 mb-2" />
          <h1 className="text-xl font-bold">OAKYA IT SERVICES PVT LTD</h1>
          <p className="text-gray-600">Course Fee Invoice</p>
          <p className="text-sm text-gray-500 mt-1">SANJAY GHODAWAT UNIVERSITY, Kolhapur, Maharashtra</p>
          <p className="text-sm text-black font-semibold mt-1">{copyType}</p>
          {isHistory && (
            <p className="text-sm text-orange-600 font-semibold mt-1">(Payment History Invoice)</p>
          )}
        </div>

        {/* Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
          <div>
            <h2 className="font-semibold mb-2">Billed To:</h2>
            <p><strong>Admission Number:</strong> {student.student_admission_number}</p>
            <p><strong>Name:</strong> {student.full_name}</p>
            <p><strong>Father Name:</strong> {student.father_name}</p>
            <p><strong>Mobile:</strong> {student.mobile_number}</p>
            <p><strong>Email:</strong> {student.email_id || 'N/A'}</p>
          </div>
          <div className="text-right sm:text-left mt-6 sm:mt-0">
            <p><strong>Invoice Date:</strong> {formatDate(displayData.payment_date || student.created_date)}</p>
            <p><strong>Invoice Number:</strong> {displayData.invoice_number || 'N/A'}</p>
            <p><strong>Course:</strong> {student.course}</p>
            <p><strong>Year:</strong> {student.year}</p>
            <p><strong>Semester:</strong> {student.semester}</p>
          </div>
        </div>

        {/* Invoice Table */}
        <div className="overflow-x-auto mb-8">
          <table className="w-full border border-gray-300">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-4 py-2 text-left">Description</th>
                <th className="border px-4 py-2 text-left">Details</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border px-4 py-2">Transaction ID</td>
                <td className="border px-4 py-2">{displayData.transaction_id || 'N/A'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">Payment Method</td>
                <td className="border px-4 py-2">{displayData.payment_method || 'N/A'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">Payment Date</td>
                <td className="border px-4 py-2">{formatDate(displayData.payment_date)}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">Due Date</td>
                <td className="border px-4 py-2">{formatDate(displayData.due_date)}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">Total Fee</td>
                <td className="border px-4 py-2">{displayINRCurrency(displayData.total_fee || 0)}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">Paid Fee</td>
                <td className="border px-4 py-2 text-green-600">{displayINRCurrency(displayData.paid_fee || 0)}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">Other Charges</td>
                <td className="border px-4 py-2">{displayINRCurrency(displayData.other_charges || 0)}</td>
              </tr>
              <tr className="bg-gray-50 font-semibold">
                <td className="border px-4 py-2">Balance Fee</td>
                <td className="border px-4 py-2 text-red-600">{displayINRCurrency(displayData.balance_fee || 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Notes */}
        <div className="mb-8 text-sm text-gray-600">
          <p className="mb-1"><strong>Payment Terms:</strong> One-time payment for the full duration of the course.</p>
          <p><strong>Note:</strong> Please retain this invoice for your records.</p>
          {isHistory && (
            <p className="mt-2 text-orange-600"><strong>Note:</strong> This is a historical payment record.</p>
          )}
        </div>

        {/* Signature */}
        <div className="mt-12 flex justify-between items-end">
          <div className="text-sm text-gray-600">
            <p>Authorized Signature</p>
            <div className="border-t border-gray-400 w-40 mt-2" />
          </div>
          <div className="text-sm text-gray-600 text-right">
            <p>Thank you for enrolling!</p>
            <p className="mt-1">Contact: +91-XXXXXXXXXX | support@oakya.com</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentInvoice;